import { useState, useMemo, useEffect } from 'react';
import {
  FULFILLMENT_METHOD_STH,
  FULFILLMENT_METHOD_BOPIS,
  attachLaborType,
  cartBinding
} from '../utils/product-add-on-constants';

export function useAttachLabor({
  fulfillment,
  availableFulfillments,
  inStoreAssemblyAvailable,
  isInstallationSku,
  type,
  priceIncludes,
  price,
  installProcess,
  onChange,
  itemId,
  isTwoTileEnabled,
}) {

  const [checked, setChecked] = useState(false);

  const {
    IN_HOME_INSTALL,
    IN_HOME_ASSEMBLY,
    IN_STORE_ASSEMBLY,
    HOME_AND_STORE_ASSEMBLY
  } = useMemo(() => {

    const inHomeInstall = isTwoTileEnabled ? (type === attachLaborType.INSTALL
      && isInstallationSku) : (
      type === attachLaborType.INSTALL
      && isInstallationSku
      && availableFulfillments.includes(FULFILLMENT_METHOD_STH));
    const inStoreAssembly = isTwoTileEnabled ? (!isInstallationSku
      && inStoreAssemblyAvailable) : (
      !isInstallationSku
      && inStoreAssemblyAvailable
      && availableFulfillments.includes(FULFILLMENT_METHOD_BOPIS)) || (
      inStoreAssemblyAvailable
        && isInstallationSku
        && !availableFulfillments.includes(FULFILLMENT_METHOD_STH)
        && type === attachLaborType.ASSEMBLY
    );
    const inHomeAssembly = isTwoTileEnabled ? (type === attachLaborType.ASSEMBLY
      && isInstallationSku
      && !inStoreAssemblyAvailable) : (
      type === attachLaborType.ASSEMBLY
      && isInstallationSku
      && !inStoreAssemblyAvailable
      && availableFulfillments.includes(FULFILLMENT_METHOD_STH)) || (
      inStoreAssemblyAvailable
      && isInstallationSku
      && !availableFulfillments.includes(FULFILLMENT_METHOD_BOPIS)
      && type === attachLaborType.ASSEMBLY
    );
    const homeAndStoreAssembly = isTwoTileEnabled ? (inStoreAssemblyAvailable && isInstallationSku) : (
      inStoreAssemblyAvailable && isInstallationSku
      && availableFulfillments.includes(FULFILLMENT_METHOD_STH)
      && availableFulfillments.includes(FULFILLMENT_METHOD_BOPIS)
    );

    return {
      IN_HOME_INSTALL: inHomeInstall,
      IN_HOME_ASSEMBLY: inHomeAssembly,
      IN_STORE_ASSEMBLY: inStoreAssembly,
      HOME_AND_STORE_ASSEMBLY: homeAndStoreAssembly
    };

  }, [availableFulfillments, inStoreAssemblyAvailable, isInstallationSku, type]);

  const heading = useMemo(
    () => ((type === attachLaborType.INSTALL) ? 'Need This Installed?' : 'Need This Assembled?'),
    [type]
  );

  const subHeading = useMemo(
    function () {
      const subHeadingText = {
        shipToHomeOnly: 'Available only with Ship to Home',
        storePickUpOnly: 'Available only with Store Pickup',
        freeWithStorePickUp: 'Free with Store Pickup'
      };
      if (IN_HOME_INSTALL || IN_HOME_ASSEMBLY) {
        return subHeadingText.shipToHomeOnly;
      }

      if (IN_STORE_ASSEMBLY) {
        return subHeadingText.storePickUpOnly;
      }

      if (HOME_AND_STORE_ASSEMBLY) {
        return subHeadingText.freeWithStorePickUp;
      }
      return '';
    },
    [IN_HOME_ASSEMBLY, IN_HOME_INSTALL, IN_STORE_ASSEMBLY, HOME_AND_STORE_ASSEMBLY, fulfillment]
  );

  const userOptions = useMemo(() => {
    const options = {
      inHomeInstall: [{ value: cartBinding.ATTACH_LABOR, checked, price, label: 'Add In-Home Installation /' }],
      inHomeAssembly: [{ value: cartBinding.ATTACH_LABOR, checked, price, label: 'Add In-Home Assembly /' }],
      inStoreAssembly: [{ value: cartBinding.ATTACH_LABOR_STORE_ASSEMBLY,
        checked,
        price: 0,
        label: 'Add In-Store Assembly /' }]
    };

    if (IN_HOME_INSTALL) {
      return options.inHomeInstall;
    }
    if (IN_STORE_ASSEMBLY) {
      return options.inStoreAssembly;
    }
    if (HOME_AND_STORE_ASSEMBLY && fulfillment === FULFILLMENT_METHOD_BOPIS) {
      return options.inStoreAssembly;
    }
    return options.inHomeAssembly;

  }, [checked, price, fulfillment, IN_HOME_INSTALL, IN_STORE_ASSEMBLY, HOME_AND_STORE_ASSEMBLY]);

  const displayDisabledText = useMemo(() => {
    const DOES_NOT_APPLY_COPY = {
      inHomeInstall: 'We\'re sorry, you can only get this product installed with Ship To Home',
      // eslint-disable-next-line max-len
      inHomeAssembly: isTwoTileEnabled ? 'We\'re sorry, you can only get this product assembled with Delivery.' : 'We\'re sorry, you can only get this product assembled with Ship To Home',
      inStoreAssembly: 'We\'re sorry, you can only get this product assembled with Store Pickup',
      storeAndHomeAssembly: 'We\'re sorry, you can only get this product assembled with Ship To Home or Store Pickup',
    };

    if (checked) {
      if (IN_HOME_INSTALL && fulfillment !== FULFILLMENT_METHOD_STH) {
        return DOES_NOT_APPLY_COPY.inHomeInstall;
      }
      if (IN_STORE_ASSEMBLY && fulfillment !== FULFILLMENT_METHOD_BOPIS) {
        return DOES_NOT_APPLY_COPY.inStoreAssembly;
      }
      if (
        HOME_AND_STORE_ASSEMBLY
        && ![FULFILLMENT_METHOD_STH, FULFILLMENT_METHOD_BOPIS].includes(fulfillment)
      ) {
        return DOES_NOT_APPLY_COPY.storeAndHomeAssembly;
      }
      if (
        IN_HOME_ASSEMBLY
        && fulfillment !== FULFILLMENT_METHOD_STH
      ) {
        return DOES_NOT_APPLY_COPY.inHomeAssembly;
      }
      return '';
    }
    return '';
    // eslint-disable-next-line max-len
  }, [checked, fulfillment, IN_HOME_ASSEMBLY, IN_HOME_INSTALL, IN_STORE_ASSEMBLY, HOME_AND_STORE_ASSEMBLY, isTwoTileEnabled]);

  const toolTipContentHeading = useMemo(
    () => {
      const headingTexts = {
        install: 'Installation Process',
        assembly: 'Assembly Process'
      };
      return type === attachLaborType.INSTALL ? headingTexts.install : headingTexts.assembly;
    },
    [type]);
  const contentList = useMemo(() => {
    if (IN_STORE_ASSEMBLY || (HOME_AND_STORE_ASSEMBLY && fulfillment === FULFILLMENT_METHOD_BOPIS)) {
      return [];
    }
    return priceIncludes;
  }, [IN_STORE_ASSEMBLY, HOME_AND_STORE_ASSEMBLY, fulfillment, priceIncludes]);

  const descriptionList = useMemo(() => {
    const TOOLTIP_DESCRIPTION_LIST = [
      { longText: `Assembly times vary. Some orders will be ready within a few hours,
      but others may take up to one week. We'll call you when your order is ready` }
    ];

    if (IN_STORE_ASSEMBLY || (HOME_AND_STORE_ASSEMBLY && fulfillment === FULFILLMENT_METHOD_BOPIS)) {
      return TOOLTIP_DESCRIPTION_LIST;
    }
    return installProcess;
  }, [IN_STORE_ASSEMBLY, HOME_AND_STORE_ASSEMBLY, fulfillment, installProcess]);

  useEffect(() => {
    if (checked) {
      // Handle when user toggles between STH and BOPIS on Store/Home Assembly scenario.
      if (HOME_AND_STORE_ASSEMBLY) {
        const addOnType = fulfillment === FULFILLMENT_METHOD_BOPIS
          ? cartBinding.ATTACH_LABOR_STORE_ASSEMBLY : cartBinding.ATTACH_LABOR;
        if (fulfillment === FULFILLMENT_METHOD_BOPIS) {
          onChange({
            checked: true,
            addOnType
          });
        } else if (fulfillment === FULFILLMENT_METHOD_STH) {
          onChange({
            checked: true,
            addOnType,
            itemId
          });
        } else {
          onChange({
            checked: true,
            addOnType,
            itemId
          });
        }
      }
      if (IN_STORE_ASSEMBLY) {
        const addOnType = cartBinding.ATTACH_LABOR_STORE_ASSEMBLY;
        if (fulfillment === FULFILLMENT_METHOD_BOPIS) {
          onChange({
            checked: true,
            addOnType,
            itemId
          });

        } else {
          onChange({
            checked: false,
            addOnType,
            itemId
          });
        }
        return;
      }
      if (IN_HOME_INSTALL || IN_HOME_ASSEMBLY) {
        const addOnType = cartBinding.ATTACH_LABOR;
        if (fulfillment === FULFILLMENT_METHOD_STH) {
          onChange({
            checked: true,
            addOnType,
            itemId
          });
        } else {
          onChange({
            checked: false,
            addOnType,
            itemId
          });
        }
      }
    } else if ((HOME_AND_STORE_ASSEMBLY && fulfillment === FULFILLMENT_METHOD_BOPIS) || IN_STORE_ASSEMBLY) {
      onChange({
        checked: false,
        addOnType: cartBinding.ATTACH_LABOR_STORE_ASSEMBLY,
      });
    } else {
      onChange({
        checked: false,
        addOnType: cartBinding.ATTACH_LABOR,
        itemId
      });
    }
  }, [HOME_AND_STORE_ASSEMBLY, IN_STORE_ASSEMBLY, checked, itemId, fulfillment]);

  return {
    heading,
    subHeading,
    userOptions,
    toolTipContentHeading,
    contentList,
    descriptionList,
    displayDisabledText,
    checked,
    setChecked
  };
}
